import { useState, useEffect } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:      <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:     <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    CheckCircle:    <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Wallet:         <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    Calendar:       <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Star:           <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Shield:         <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    ShieldCheck:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:     <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Users:          <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    MapPin:         <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Smartphone:     <><rect width="14" height="20" x="5" y="2" rx="2"/><path d="M12 18h.01"/></>,
    Clock:          <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Zap:            <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    TrendingUp:     <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Wrench:         <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Home:           <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Droplets:       <><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></>,
    Plug:           <><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 0 0-2 2v2a7 7 0 1 0 14 0v-2a2 2 0 0 0-2-2Z"/></>,
    Paintbrush:     <><path d="M18.37 2.63 14 7l-1.59-1.59a2 2 0 0 0-2.82 0L8 7l9 9 1.59-1.59a2 2 0 0 0 0-2.82L17 10l4.37-4.37a2.12 2.12 0 1 0-3-3z"/><path d="M9 8c-2 2.5-2 5-2 7s2 4 2 6"/></>,
    Scissors:       <><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" x2="8.12" y1="4" y2="15.88"/><line x1="14.47" x2="20" y1="14.48" y2="20"/><line x1="8.12" x2="12" y1="8.12" y2="12"/></>,
    Truck:          <><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect width="7" height="7" x="14" y="10" rx="1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Leaf:           <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    Camera:         <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></>,
    Lock:           <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    ChevronDown:    <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:      <><path d="m18 15-6-6-6 6"/></>,
    User:           <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    FileText:       <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    ChevronRight:   <><path d="m9 18 6-6-6-6"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Data ── */
const SERVICES = [
  { icon: "Droplets",   label: "Plumbing",           range: "KES 1,500 – 8,000",  note: "per job" },
  { icon: "Plug",       label: "Electrical",          range: "KES 2,000 – 10,000", note: "per job" },
  { icon: "Home",       label: "Cleaning",            range: "KES 1,200 – 5,000",  note: "per session" },
  { icon: "Paintbrush", label: "Painting",            range: "KES 500 – 1,200",    note: "per sq. metre" },
  { icon: "Leaf",       label: "Gardening",           range: "KES 1,500 – 6,000",  note: "per session" },
  { icon: "Wrench",     label: "Appliance Repair",    range: "KES 1,000 – 5,000",  note: "per job" },
  { icon: "Scissors",   label: "Carpentry",           range: "KES 2,000 – 15,000", note: "per job" },
  { icon: "Camera",     label: "Security Systems",    range: "KES 5,000 – 25,000", note: "per installation" },
  { icon: "Truck",      label: "Moving & Relocation", range: "KES 3,000 – 20,000", note: "per move" },
];

const ONBOARDING_STEPS = [
  {
    n: 1, icon: "User", title: "Personal information",
    desc: "Your full name, phone number, and the city you operate in. This takes under two minutes. Your phone number becomes your login; no separate username to remember.",
    detail: "We use your phone number to send you booking notifications via SMS so you never miss a job, even with patchy data.",
  },
  {
    n: 2, icon: "Wrench", title: "Services you offer",
    desc: "Select every category you work in. You can offer one service or many. Be accurate — clients search by service type and your profile only appears for the services you list.",
    detail: "You can add or remove services later from your Pro dashboard. Starting with what you're most confident in is usually the right call.",
  },
  {
    n: 3, icon: "Wallet", title: "Your pricing",
    desc: "Set your rates for each service. You choose your own prices — JiraniApp does not set them for you. You can list a fixed rate, an hourly rate, or a 'quote on request' option for jobs that vary.",
    detail: "Transparent pricing upfront leads to more bookings. Clients on JiraniApp filter by price range, so competitive rates are visible immediately.",
  },
  {
    n: 4, icon: "BadgeCheck", title: "Identity verification",
    desc: "Upload a photo of your national ID or passport. This is how we verify you are who you say you are. Verification is reviewed by our team within 24–48 hours.",
    detail: "Verified Pros receive a badge on their profile that clients can see. Unverified profiles cannot accept paid bookings. This step protects both you and the clients you'll serve.",
  },
  {
    n: 5, icon: "Calendar", title: "Availability",
    desc: "Set the days and hours you're available for bookings. You can update this any time from your dashboard. Clients only see you as available when you say you are.",
    detail: "You stay in control of your schedule. Clients cannot book you outside the times you set, and you can block out days, set recurring availability, or switch to 'unavailable' instantly.",
  },
  {
    n: 6, icon: "CheckCircle", title: "Review and submit",
    desc: "Review everything before your profile goes live. Once submitted, our team reviews your ID verification and activates your account. You'll receive an SMS when you're approved.",
    detail: "Most profiles are reviewed and approved within 24 hours on business days. Once active, you'll immediately appear in search results for clients in your area.",
  },
];

const FAQS = [
  {
    q: "Is there a fee to join JiraniApp as a Pro?",
    a: "No. Signing up and creating your profile is completely free. JiraniApp charges a small platform commission on completed bookings — typically 10–15% depending on the service category. This covers payment processing, client acquisition, platform support, and your listing. You keep the rest.",
  },
  {
    q: "How and when do I get paid?",
    a: "Payment is released to your M-Pesa within 24 hours of a job being marked complete and confirmed by the client. For jobs paid upfront through the platform, funds are held securely and released on completion. You can see your full payout history and pending amounts in your Pro dashboard.",
  },
  {
    q: "Can I work in multiple cities or areas?",
    a: "Yes. During setup you define your service area — you can cover multiple estates, a full city, or multiple cities if you travel. Clients searching in any area you cover will see your profile. You can update your service area at any time.",
  },
  {
    q: "What happens if a client cancels?",
    a: "If a client cancels more than 24 hours before a scheduled job, no compensation applies. If a client cancels within 24 hours of the scheduled time, you may be entitled to a cancellation fee, which is reviewed on a case-by-case basis. Repeat late cancellations by clients are flagged on their account.",
  },
  {
    q: "Do I need to have formal qualifications?",
    a: "It depends on the service. For regulated work like electrical installations or gas plumbing, we require proof of relevant certification. For services like cleaning, gardening, or moving, no formal qualification is needed — we verify your identity and rely on your track record and client reviews to build your reputation.",
  },
  {
    q: "Can I set my own prices?",
    a: "Yes, entirely. JiraniApp does not set your rates. You decide what to charge per service, whether that is a fixed price, an hourly rate, or a custom quote for larger jobs. The platform gives you market context so you can price competitively, but the final decision is yours.",
  },
  {
    q: "What if a client raises a dispute against me?",
    a: "If a client opens a dispute, you will be notified and given the opportunity to provide your account of events. Our team reviews both sides fairly before making any determination. Disputes that are unfounded do not affect your profile. Legitimate issues are resolved through our standard process — your cooperation in disputes reflects positively on your standing.",
  },
  {
    q: "Can I pause or deactivate my profile?",
    a: "Yes. You can set yourself as unavailable at any time from your dashboard, which removes you from search results without deleting your profile. If you need a longer break, you can deactivate your account and reactivate it when you're ready to take bookings again.",
  },
];

/* ── FAQ accordion ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 400 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 15, color: T.n600, lineHeight: 1.75, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ── Onboarding step ── */
function OnboardingStep({ step, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 19, top: 44, bottom: open ? -8 : -24, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})`, transition: "bottom .3s" }} />
      )}
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={step.icon} size={16} color={T.g600} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 36 }}>
        <button onClick={() => setOpen(o => !o)} style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          padding: 0, textAlign: "left", fontFamily: T.sans,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", marginBottom: 5 }}>STEP {step.n}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{step.title}</div>
          </div>
          <span style={{
            width: 24, height: 24, borderRadius: "50%", flexShrink: 0, marginTop: 14,
            border: `1px solid ${open ? T.g500 : T.n200}`,
            background: open ? T.g500 : "transparent",
            color: open ? T.white : T.n400,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, fontWeight: 300, lineHeight: 1,
            transition: "all .2s", transform: open ? "rotate(45deg)" : "none",
          }}>+</span>
        </button>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: "8px 0 0" }}>{step.desc}</p>
        <div style={{ overflow: "hidden", maxHeight: open ? 120 : 0, transition: "max-height .3s cubic-bezier(.4,0,.2,1)" }}>
          <div style={{ marginTop: 12, padding: "12px 14px", background: T.g50, borderRadius: 10, border: `1px solid ${T.g100}` }}>
            <p style={{ fontSize: 13, color: T.g700, lineHeight: 1.7, margin: 0 }}>{step.detail}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Service earning card ── */
function ServiceCard({ icon, label, range, note }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "18px 16px", borderRadius: 14,
        border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", cursor: "default",
      }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, transition: "all .18s" }}>
        <Icon name={icon} size={16} color={hovered ? T.g600 : T.n400} strokeWidth={1.7} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.n800, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 400, color: hovered ? T.g700 : T.n900, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{range}</div>
      <div style={{ fontSize: 11, color: T.n400, marginTop: 2 }}>{note}</div>
    </div>
  );
}

/* ── Testimonial ── */
function Testimonial({ name, area, service, text, earnings }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const colors = [
    { bg: T.g50, text: T.g700, border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = colors[name.charCodeAt(0) % colors.length];
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{name}</div>
            <div style={{ fontSize: 12, color: T.n400 }}>{service} · {area}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: T.serif, fontSize: 18, color: T.g600, fontWeight: 400, letterSpacing: "-0.02em" }}>{earnings}</div>
          <div style={{ fontSize: 11, color: T.n400 }}>avg. monthly earnings</div>
        </div>
      </div>
    </div>
  );
}

/* ── Sticky CTA bar ── */
function StickyBar({ onSignUp }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
      background: T.white, borderTop: `1px solid ${T.n150}`,
      padding: "14px 20px", display: "flex", alignItems: "center",
      justifyContent: "space-between", gap: 12,
      boxShadow: "0 -4px 24px rgba(0,0,0,.08)",
      transform: visible ? "translateY(0)" : "translateY(100%)",
      transition: "transform .3s cubic-bezier(.4,0,.2,1)",
    }}>
      <div style={{ fontFamily: T.sans }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900 }}>Ready to start earning?</div>
        <div style={{ fontSize: 12, color: T.n400 }}>Free to join · Set your own rates</div>
      </div>
      <button onClick={onSignUp} style={{
        padding: "11px 22px", background: T.g500, color: T.white,
        border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
        fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
        whiteSpace: "nowrap", flexShrink: 0,
      }}
        onMouseEnter={e => e.currentTarget.style.background = T.g600}
        onMouseLeave={e => e.currentTarget.style.background = T.g500}>
        Join free →
      </button>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function BecomeAProPage() {
  useFonts();
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();

  const goToSignUp  = () => navigate("/signup");
  const goToLogin   = () => openLogin();

  const TESTIMONIALS = [
    {
      name: "James K.", area: "Westlands, Nairobi", service: "Electrician",
      earnings: "KES 78,000",
      text: "I used to find work through word of mouth and it was unpredictable. Some months were great, others were dry. Since joining JiraniApp I have consistent bookings every week and I can actually plan my income. The platform does the marketing, I just show up and do the work.",
    },
    {
      name: "Mary W.", area: "Kilimani, Nairobi", service: "Cleaner",
      earnings: "KES 42,000",
      text: "I was hesitant about apps but the sign-up was simple and I got my first booking within three days of being approved. The clients are serious, they book, they pay, they leave reviews. I have built up 40 reviews now and the bookings come on their own.",
    },
    {
      name: "Ali H.", area: "Nyali, Mombasa", service: "Plumber",
      earnings: "KES 65,000",
      text: "What I appreciate most is that payment goes straight to M-Pesa after the job. No chasing clients, no awkward conversations about money. I set my price, the client agrees at booking, and the money arrives. That alone is worth it.",
    },
  ];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 80 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={goToLogin} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="User" size={13} strokeWidth={2} />
          Already a JiraniPro? Sign in
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="BadgeCheck" size={14} color={T.g500} strokeWidth={2.2} />
            For service professionals
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,70px)", fontWeight: 400, margin: "0 0 22px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 640 }}>
            Grow your business<br/>
            as a <em style={{ fontStyle: "italic", color: T.g600 }}>JiraniPro</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 36px", maxWidth: 500 }}>
            Join hundreds of verified home service professionals earning consistently across Kenya. 
            You set your rates, choose your hours, and build your reputation; we bring the clients.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Join free — it takes 10 minutes
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
            </button>
            <span style={{ fontSize: 13, color: T.n400 }}>No registration fee · No monthly cost</span>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "Users",      label: "700+ active JiraniPros across Kenya" },
            { icon: "Wallet",     label: "M-Pesa payout within 24h of job completion" },
            { icon: "Star",       label: "Verified badge builds client trust faster" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "22px 20px", display: "flex", alignItems: "center", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={18} color={T.g500} strokeWidth={1.5} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ WHY JIRANI ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Why JiraniApp</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 32px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What working on JiraniApp <em style={{ fontStyle: "italic", color: T.g600 }}>actually means</em>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
            {[
              { icon: "TrendingUp", title: "Consistent work, not just occasional jobs", desc: "Clients on JiraniApp book repeatedly. A good first job leads to rebookings, direct messages, and referrals within the platform. Your calendar fills over time without you having to find each job individually." },
              { icon: "Wallet",     title: "You set your own prices", desc: "JiraniApp does not tell you what to charge. You decide your rates per service, and clients see them upfront. You can adjust pricing any time from your dashboard based on your experience and demand." },
              { icon: "Clock",      title: "Work when you want to work", desc: "Set your availability by day and time. Clients can only book you within the hours you define. You can go unavailable instantly, block specific dates, or set recurring weekly availability." },
              { icon: "BadgeCheck", title: "A verified badge clients trust", desc: "After identity verification, your profile shows a JiraniPro Verified badge. This single mark meaningfully increases the rate at which clients choose your profile over an unverified one." },
              { icon: "Smartphone", title: "Everything from your phone", desc: "Accept bookings, message clients, track your earnings, and manage your schedule — all from the JiraniApp on your phone. No desktop required, no paperwork, no separate invoicing." },
              { icon: "ShieldCheck",title: "Protected on every job", desc: "JiraniApp's Trust & Safety team handles client disputes fairly. Unfounded complaints do not affect your profile. You always have the opportunity to give your account of events before any determination is made." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "22px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.white, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={16} color={T.g500} strokeWidth={1.7} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em" }}>{item.title}</div>
                <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ EARNINGS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What you can earn</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Real earning ranges across <em style={{ fontStyle: "italic", color: T.g600 }}>Kenya</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            These are representative ranges based on jobs completed on the platform. What you earn depends on your service, location, availability, and reputation. Pros with strong reviews and high availability consistently earn at the top of these ranges.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
            {SERVICES.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
          <div style={{ padding: "16px 20px", borderRadius: 12, background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>ℹ</span>
            <p style={{ fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.65, fontFamily: T.sans }}>
              Ranges reflect individual job earnings before the JiraniApp platform commission (10–15%). 
              Monthly income depends on the number of jobs you take on and your service area.
              Top-earning Pros in Nairobi take home KES 60,000–120,000 monthly working full-time.
            </p>
          </div>
        </div>

        {/* ══ ONBOARDING STEPS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Getting started</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Six steps, about <em style={{ fontStyle: "italic", color: T.g600 }}>10 minutes</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            The sign-up process is designed to be completed on your phone in a single sitting. 
            Tap any step to see what's involved before you start.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {ONBOARDING_STEPS.map((step, i) => (
              <OnboardingStep key={i} step={step} isLast={i === ONBOARDING_STEPS.length - 1} />
            ))}
          </div>
          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start your application
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <span style={{ fontSize: 13, color: T.n400 }}>Approved within 24 hours on business days</span>
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>From JiraniPros</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            In their own <em style={{ fontStyle: "italic", color: T.g600 }}>words</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            From professionals already working on the platform across Kenya.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <Testimonial key={i} {...t} />)}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>sign up</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            The most common things professionals ask us before joining.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Zap" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(28px,4.5vw,40px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your next client is<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>already searching</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 32px", lineHeight: 1.75, maxWidth: 400 }}>
            Join free. Set your rates. Start getting booked. 
            The whole process takes 10 minutes and approval arrives within 24 hours.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSignUp} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Create your Pro profile
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToLogin} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Sign in to existing account
            </button>
          </div>
          <p style={{ fontSize: 12, color: T.n300, margin: "20px 0 0" }}>
            Free to join · No monthly fees · Commission only on completed bookings
          </p>
        </div>

      </div>

      <StickyBar onSignUp={goToSignUp} />
    </div>
  );
}